// frontend/src/components/video/VideoPlayerModal.tsx
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type SyntheticEvent,
} from "react";
import {
  AlertCircle,
  Download,
  Loader2,
  Maximize2,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/Modal";
import SegmentStrip from "@/components/video/SegmentStrip";
import { useVideoControls } from "@/components/video/controls/useVideoControls";
import {
  isNativeKeyTarget,
  resolveVideoKeyAction,
} from "@/components/video/controls/keymap";
import { apiClient } from "@/services/api";
import { openPresignedDownload } from "@/utils/download";
import type { VideoFile, VideoResolutionOption } from "@/types";

interface VideoPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Segments play back-to-back in array order; a single video is a one-element playlist. */
  playlist: VideoFile[];
  /** Called when a segment fails to load; lets the host toast a message. */
  onSegmentError?: (video: VideoFile) => void;
  /** Hide the download action where the viewer is not the session owner. */
  canDownload?: boolean;
}

function selectBestResolution(resolutions: VideoResolutionOption[]): string | null {
  const available = resolutions.filter((r) => r.available);
  if (available.length === 0) return null;
  return [...available].sort(
    (a, b) => parseInt(b.resolution, 10) - parseInt(a.resolution, 10),
  )[0].resolution;
}

export default function VideoPlayerModal({
  isOpen,
  onClose,
  playlist,
  onSegmentError,
  canDownload = true,
}: VideoPlayerModalProps) {
  const { t } = useTranslation(["pages", "common"]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerContainerRef = useRef<HTMLDivElement | null>(null);
  const [resolutions, setResolutions] = useState<VideoResolutionOption[]>([]);
  const [selectedResolution, setSelectedResolution] = useState<string | null>(null);
  const [videoSrc, setVideoSrc] = useState<string>("");
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [urlError, setUrlError] = useState("");
  const [rawIndex, setRawIndex] = useState(0);
  const [failedIds, setFailedIds] = useState<string[]>([]);
  const shouldAutoPlayRef = useRef(false);
  // User's quality choice, carried across segment switches (ref, not state,
  // so the per-segment resolutions fetch doesn't re-run when it changes).
  const preferredResolutionRef = useRef<string | null>(null);
  const { binding, containerProps } = useVideoControls(
    videoRef,
    playerContainerRef,
    // No rate control here: play at 1x and never touch the persisted rate.
    { persistPlaybackRate: false },
  );

  // Clamped so a playlist that shrinks can never leave the render pointing
  // past the last segment.
  const activeIndex = Math.min(rawIndex, playlist.length - 1);
  const video = playlist[activeIndex] ?? null;

  // Fresh playlist on every open: start at segment 1, clear transient state.
  useEffect(() => {
    if (isOpen) {
      setRawIndex(0);
      setFailedIds([]);
      shouldAutoPlayRef.current = false;
      preferredResolutionRef.current = null;
    }
  }, [isOpen]);

  // Fetch available resolutions when the modal opens
  useEffect(() => {
    if (!isOpen || !video) return;

    let cancelled = false;

    setResolutions([]);
    setSelectedResolution(null);
    setVideoSrc("");
    setUrlError("");

    apiClient
      .getVideoResolutions(video.id)
      .then((data) => {
        if (cancelled) return;
        setResolutions(data.resolutions);
        const available = data.resolutions.filter((r) => r.available);
        const preferred = preferredResolutionRef.current;
        const keep =
          preferred && available.some((r) => r.resolution === preferred)
            ? preferred
            : selectBestResolution(data.resolutions);
        if (keep) {
          // Sync the ref so an unavailable preference is replaced by the
          // fallback the user actually sees.
          preferredResolutionRef.current = keep;
          setSelectedResolution(keep);
        } else {
          // No processed versions ready — use original
          setVideoSrc(video.url);
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Failed to reach the resolutions endpoint — fall back to original
        setVideoSrc(video.url);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, video]);

  // Fetch presigned playback URL whenever the selected resolution changes
  useEffect(() => {
    if (!video || !selectedResolution) return;

    let cancelled = false;

    setIsLoadingUrl(true);
    setUrlError("");

    apiClient
      .getVideoPlaybackUrl(video.id, selectedResolution)
      .then((data) => {
        if (cancelled) return;
        setVideoSrc(data.playbackUrl);
      })
      .catch(() => {
        if (cancelled) return;
        setUrlError(t("pages:sessions.videoPlayer.loadError"));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingUrl(false);
      });

    return () => {
      cancelled = true;
    };
  }, [video, selectedResolution, t]);

  const handleDownload = async () => {
    if (!video) return;
    try {
      const data = await apiClient.getVideoDownloadUrl(video.id);
      openPresignedDownload(data.downloadUrl);
    } catch {
      openPresignedDownload(video.url);
    }
  };

  const goToSegment = useCallback(
    (index: number) => {
      const next = Math.max(0, Math.min(index, playlist.length - 1));
      if (next === activeIndex) return;
      shouldAutoPlayRef.current = true;
      setRawIndex(next);
    },
    [activeIndex, playlist.length],
  );

  const handleEnded = useCallback(() => {
    if (activeIndex < playlist.length - 1) {
      goToSegment(activeIndex + 1);
    }
  }, [activeIndex, goToSegment, playlist.length]);

  const handleVideoError = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      // Ignore late errors from an outgoing (detached) element so only the
      // active segment can be marked as failed.
      if (videoRef.current && event.currentTarget !== videoRef.current) return;
      const failed = playlist[activeIndex];
      if (!failed) return;
      setFailedIds((prev) =>
        prev.includes(failed.id) ? prev : [...prev, failed.id],
      );
      onSegmentError?.(failed);
      if (activeIndex < playlist.length - 1) {
        goToSegment(activeIndex + 1);
      }
    },
    [activeIndex, goToSegment, onSegmentError, playlist],
  );

  // Large seeks navigate segments (the modal's "large" navigation unit);
  // every other resolved action falls through to the shared container
  // handler from useVideoControls. Keys aimed at native controls (the seek
  // and volume sliders) are left untouched by both.
  const handleContainerKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (
        playlist.length > 1 &&
        !event.repeat &&
        !event.defaultPrevented &&
        !isNativeKeyTarget(event.target)
      ) {
        const action = resolveVideoKeyAction(event);
        if (action === "seek-forward-large") {
          event.preventDefault();
          goToSegment(activeIndex + 1);
          return;
        }
        if (action === "seek-back-large") {
          event.preventDefault();
          goToSegment(activeIndex - 1);
          return;
        }
      }
      containerProps.onKeyDown?.(event);
    },
    [activeIndex, containerProps, goToSegment, playlist.length],
  );

  const formatTime = (time: number) => {
    if (!Number.isFinite(time) || time < 0) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  if (playlist.length === 0) return null;

  const availableResolutions = resolutions.filter((r) => r.available);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("pages:sessions.videoPlayer.title")} size="xl">
      {/* Sub-header: filename, quality selector, download */}
      <div className="-mt-6 -mx-6 px-6 py-3 bg-secondary-50 border-b border-secondary-200 flex items-center justify-between gap-4 mb-4">
        <span className="text-sm text-secondary-500 font-mono truncate min-w-0">
          {video.filename}
        </span>
        <div className="flex items-center gap-3 flex-shrink-0">
          {availableResolutions.length > 0 && (
            <div className="flex items-center gap-2">
              <label
                htmlFor="video-quality-select"
                className="text-xs text-secondary-500"
              >
                {t("pages:sessions.videoPlayer.quality")}
              </label>
              <select
                id="video-quality-select"
                value={selectedResolution ?? ""}
                onChange={(e) => {
                  preferredResolutionRef.current = e.target.value;
                  setSelectedResolution(e.target.value);
                }}
                className="text-xs border border-secondary-300 rounded px-2 py-1 text-secondary-700 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {availableResolutions.map((r) => (
                  <option key={r.resolution} value={r.resolution}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {canDownload && (
            <button
              onClick={handleDownload}
              title={t("pages:sessions.videoPlayer.downloadTitle")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-secondary-300 rounded text-secondary-600 hover:text-secondary-800 hover:border-secondary-400 bg-white transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              {t("pages:sessions.videoPlayer.download")}
            </button>
          )}
        </div>
      </div>

      {/* Video area */}
      <div
        ref={playerContainerRef}
        data-testid="video-player-container"
        tabIndex={containerProps.tabIndex}
        onKeyDown={handleContainerKeyDown}
        className="rounded-lg overflow-hidden border border-secondary-200 bg-white"
      >
        <div className="bg-black aspect-video flex items-center justify-center">
          {isLoadingUrl ? (
            <div className="flex items-center gap-2 text-white">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-sm">{t("pages:sessions.videoPlayer.loadingVideo")}</span>
            </div>
          ) : urlError ? (
            <div className="flex flex-col items-center gap-2 text-red-400">
              <AlertCircle className="w-8 h-8" />
              <span className="text-sm">{urlError}</span>
            </div>
          ) : videoSrc ? (
            <video
              key={videoSrc}
              ref={videoRef}
              src={videoSrc}
              poster={video.thumbnailUrl ?? undefined}
              playsInline
              aria-label={t("pages:sessions.videoPlayer.ariaPlayerFor", { name: video.filename })}
              className="w-full h-full"
              preload="metadata"
              onEnded={handleEnded}
              onError={handleVideoError}
              onCanPlay={() => {
                if (shouldAutoPlayRef.current) {
                  shouldAutoPlayRef.current = false;
                  void videoRef.current?.play().catch(() => undefined);
                }
              }}
            />
          ) : (
            <div className="flex items-center gap-2 text-secondary-400">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-sm">{t("pages:sessions.videoPlayer.loading")}</span>
            </div>
          )}
        </div>

        <div className="border-t border-secondary-200 bg-white px-4 py-3">
          <div className="flex items-center gap-3 text-xs text-secondary-500">
            <span className="tabular-nums">{formatTime(binding.currentTime)}</span>
            <input
              type="range"
              min="0"
              max={binding.duration || 0}
              step="0.1"
              value={Math.min(binding.currentTime, binding.duration || 0)}
              onChange={(e) => binding.onSeek(Number(e.target.value))}
              aria-label={t("pages:sessions.videoPlayer.ariaSeek")}
              className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-secondary-200"
              disabled={!videoSrc || !!urlError || binding.duration <= 0}
            />
            <span className="tabular-nums">{formatTime(binding.duration)}</span>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  binding.onPlayPause();
                }}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-secondary-300 text-secondary-700 transition-colors hover:border-secondary-400 hover:text-secondary-900 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={binding.isPlaying ? t("pages:sessions.videoPlayer.ariaPause") : t("pages:sessions.videoPlayer.ariaPlay")}
                disabled={!videoSrc || !!urlError}
              >
                {binding.isPlaying ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="ml-0.5 h-4 w-4" />
                )}
              </button>

              <div className="flex items-center gap-2 rounded-full border border-secondary-200 px-3 py-2">
                <button
                  type="button"
                  onClick={binding.onToggleMute}
                  className="text-secondary-600 transition-colors hover:text-secondary-900"
                  aria-label={binding.isMuted || binding.volume === 0 ? t("pages:sessions.videoPlayer.ariaUnmute") : t("pages:sessions.videoPlayer.ariaMute")}
                  disabled={!videoSrc || !!urlError}
                >
                  {binding.isMuted || binding.volume === 0 ? (
                    <VolumeX className="h-4 w-4" />
                  ) : (
                    <Volume2 className="h-4 w-4" />
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={binding.isMuted ? 0 : binding.volume}
                  onChange={(e) => binding.onVolumeChange(Number(e.target.value))}
                  aria-label={t("pages:sessions.videoPlayer.ariaVolume")}
                  className="h-1 w-24 cursor-pointer appearance-none rounded-full bg-secondary-200"
                  disabled={!videoSrc || !!urlError}
                />
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                binding.onToggleFullscreen();
              }}
              className="inline-flex items-center gap-2 rounded-full border border-secondary-300 px-3 py-2 text-sm text-secondary-700 transition-colors hover:border-secondary-400 hover:text-secondary-900 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={t("pages:sessions.videoPlayer.ariaFullscreen")}
              disabled={!videoSrc || !!urlError}
            >
              <Maximize2 className="h-4 w-4" />
              {t("pages:sessions.videoPlayer.fullscreen")}
            </button>
          </div>
        </div>
      </div>

      {playlist.length > 1 && (
        <SegmentStrip
          segments={playlist}
          activeIndex={activeIndex}
          failedIds={failedIds}
          onSelect={goToSegment}
        />
      )}
    </Modal>
  );
}
